/**
 * A finished stretch of speech as the pieces that are translated (SEO_BRIEF_10 section 2.1):
 * one piece when it is short, which is nearly always, and pieces of at most MAX_SENTENCE
 * characters when someone talks on without a pause. `/api/live-translate` takes no more
 * (contracts/api.md, `LIVE_TEXT_TOO_LONG`), and one sentence in is one sentence back.
 *
 * A long stretch is cut at the last full stop, question mark or exclamation mark that leaves a
 * piece of a fair length; failing that at a comma, semicolon, colon or dash; failing that at a
 * space; and only text with none of them (Chinese, Japanese and Thai can run on) is cut at the
 * length itself, never through a surrogate pair. The punctuation of each script counts:
 * 。！？ ؟ । ، ؛ ， 、 and the rest below.
 */

/** The most one piece may hold, in UTF-16 code units, as the server counts it. */
export const MAX_SENTENCE = 300;

/** A cut leaves at least this share of MAX_SENTENCE behind it, so no piece is a stub. */
const MIN_SHARE = 0.3;

/** A sentence's end: the mark, any closing quote or bracket, then a space or the end. */
const STRONG = /[.!?…‼⁇⁈⁉。！？．؟۔।॥]["'”’»)\]）」』】]*(?=\s|$)/gu;
/** CJK sentence ends need no space after them. */
const STRONG_CJK = /[。！？]["'”’»)\]）」』】]*/gu;
/** A pause inside a sentence. */
const WEAK = /[,:،؛，、；：—–](?=\s|$)|[，、；：]/gu;
const SPACE = /\s+/gu;

/** The end of the last match of `pattern` in `head` that ends within from..max, or -1. */
function lastBreak(head: string, pattern: RegExp, from: number, max: number): number {
  let at = -1;
  for (const m of head.matchAll(pattern)) {
    const end = (m.index ?? 0) + m[0].length;
    if (end >= from && end <= max) at = end;
  }
  return at;
}

/** Where to cut `text` (longer than `max`): an index in 1..max. */
function cutAt(text: string, max: number): number {
  // One past the limit, so a mark at the limit is seen with what follows it.
  const head = text.slice(0, max + 1);
  const from = Math.floor(max * MIN_SHARE);
  for (const pattern of [STRONG, STRONG_CJK, WEAK]) {
    const at = lastBreak(head, pattern, from, max);
    if (at > 0) return at;
  }
  // The last space within the limit.
  let space = -1;
  for (const m of head.matchAll(SPACE)) {
    const index = m.index ?? 0;
    if (index >= from && index <= max) space = index;
  }
  if (space > 0) return space;
  // No break at all: the length itself, stepping back off a high surrogate.
  const code = text.charCodeAt(max - 1);
  return code >= 0xd800 && code <= 0xdbff ? max - 1 : max;
}

/** One finished stretch of speech, whitespace tidied, as pieces of at most `max` characters. */
export function splitSentences(text: string, max: number = MAX_SENTENCE): string[] {
  let rest = (text ?? '').replace(/\s+/g, ' ').trim();
  const out: string[] = [];
  while (rest.length > max) {
    const at = cutAt(rest, max);
    const piece = rest.slice(0, at).trim();
    if (piece) out.push(piece);
    rest = rest.slice(at).trim();
  }
  if (rest) out.push(rest);
  return out;
}
